<template>
  <div class="jps-search-box layout-col" v-if="ss">
    <div class="keyword-line">
      <component
        :is="formSize === 'middle' ? BaseSectionContainer : 'div'"
        :size="formSize"
        tag="div"
        class="keyword-line-inner"
      >
        <form
          @submit.prevent="doSearch()"
          role="search"
          class="jps-keyword-field"
        >
          <input
            ref="keyword-input"
            class="jps-input jps-keyword-input is-size-6-touch"
            :placeholder="placeholder"
            type="search"
            v-model="keyword"
            @keyup.prevent.esc="keyword = ''"
            :key="inputKeywordRefresh"
          />

          <div class="jps-keyword-search-button-list">
            <UiButtonIcon
              type="submit"
              class="jps-keyword-search-button"
              inner-class="jps-keyword-search-button-inner"
              :icon="isImageSearch ? 'mdi/image-search' : 'mdi/magnify'"
              :arialabel="ls(isImageSearch ? dapLangGlobal.imageSearch : dapLangGlobal.search)"
            />

            <slot name="search-option-button" :inputKeyword="keyword" />
          </div>
        </form>
        <slot name="search-menu-addon" />
      </component>
    </div>

    <slot name="search-settings" />

    <!-- 検索クエリ -->
    <div
      class="queries"
      aria-live="polite"
    >
      <!-- 検索の制約 -->
      <UiDbSpan
        v-for="(dbid, index) in databaseIds"
        :key="dbid"
        tag="span"
        class="jps-tag target-db jps-search-box-query-button"
        :id="dbid"
      >
        <template v-slot="{ db }">
          <template v-if="index === 0">
            {{ lss('対象データベース', 'Target databases') }}
            <span class="colon">:</span>
          </template>
          {{ ls(db.name) }}
          <span class="moredb" v-if="index === databaseIds.length - 1">{{
            moreDbMsg
          }}</span>
        </template>
      </UiDbSpan>
      <slot name="database-addon" />

      <template v-if="hasSearchConstraints">
        <span
          class="jps-tag constraint jps-search-box-query-button"
          v-if="hasSearchConstraints && !showMoreConstraints && !editable"
        >
          {{
            lss(
              '検索対象があらかじめ絞り込まれています',
              'Search result is filtered',
            )
          }}
        </span>
        <template v-if="showMoreConstraints || editable">
          <SearchSubquery
            class="constraint"
            v-for="(sq, i) in cs.constraints"
            :key="i"
            :cs="cs"
            :sub-query="sq"
            :unmodifiable="!editable"
            @edit="emits('edit-constraint', sq)"
            @delete="emits('delete-constraint', sq)"
          />
        </template>
      </template>
      <slot name="constraint-addon" />

      <button
        class="jps-text-button jps-search-box-query-button"
        v-if="hasMoreConstraints && !editable"
        @click="showMoreConstraints = !showMoreConstraints"
      >
        {{ showMoreConstraints ? ls(dapLangGlobal.hide) : ls(dapLangGlobal.show) }}
      </button>
      <span
        class="splitter"
        v-if="(databaseIds.length > 0 || hasSearchConstraints) && canReset"
      />

      <!-- クエリー -->
      <template v-for="f in querydFields">
        <template v-for="v in getValues(f.key)" :key="f.key + '-' + v.v">
          <UiTagOption
            v-if="isDisplayQueryField(f.key, v)"
            @click="deselect(f.key, v)"
            class="jps-search-box-query-button"
            size="small"
            icon="mdi/close-circle"
            :mode="isExclude(v) ? 'exclude' : 'include'"
          >
            <UiDbSpan tag="span" v-if="f.type === 'database'" :id="getDatabaseId(v.v)">
              <template v-slot="{ db }">
                {{ dbDisplay(db, v) }}
              </template>
            </UiDbSpan>
            <UiOrgSpan
              :id="getDatabaseId(v.v)"
              v-else-if="f.type === 'organization'"
            >
              <template v-slot="{ org }">
                {{ dbDisplay(org, v) }}
              </template>
            </UiOrgSpan>

            <UiOrgProviderSpan
              v-else-if="f.type === 'provider'"
              :id="getDatabaseId(v.v)"
            >
              <template v-slot="{ org }">
                {{ dbDisplay(org, v) }}
              </template>
            </UiOrgProviderSpan>
            <UiTagSpan
              v-else-if="f.type === 'tag'"
              :id="getDatabaseId(v.v)"
              notag
            >
              <template v-slot="{ tag }">
                {{ tagDisplay(tag, v) }}
              </template>
            </UiTagSpan>
            <span v-else-if="f.type === 'exist'">
              {{ lst({ ja: '{}が存在する', en: '{} exists' }, ls(f.name)) }}
            </span>
            <span v-else>
              {{ ls(f.name) }}
              <span class="colon">:</span>
              {{ display(f, v) }}
            </span>
          </UiTagOption>
        </template>
      </template>

      <!-- テキスト画像検索クエリ -->

      <button
        v-if="ss.state.text2image"
        @click="removeTextImage()"
        class="jps-tag jps-tag-ricon jps-dark jps-search-box-query-button"
      >
        {{ ls(dapLangGlobal.imageSearch) }}
        <span class="colon">:</span>
        {{ ss.state.text2image }}
        <BaseIcon size="size-6" name="mdi/close-circle" />
      </button>

      <!-- 画像検索クエリ -->
      <template v-for="i in imageInfo">
        <UiItemSpan
          :id="i.key"
          v-if="!i.external"
          class="image-search-query"
          aria-label="Image Search"
          @click="removeImage(i.key)"
        >
          <template v-slot="{ item }">
            <BaseImage
              class="selected-image"
              v-if="!isEmpty(item.common.thumbnailUrl)"
              :src="item.common.thumbnailUrl[0]"
              :error-alt="otherNoImage"
            />
            <button class="jps-tag jps-dark jps-tag-ricon jps-search-box-query-button image-search-button">
              <span class="text">{{ li(item) }}</span>
              <BaseIcon size="size-6" name="mdi/close-circle" class="icon" />
            </button>
          </template>
        </UiItemSpan>
        <span
          class="image-search-query"
          @click="removeImage(i.key)"
          v-else
        >
          <BaseImage
            class="selected-image"
            :src="i.image"
            :error-alt="otherNoImage"
          />
          <button class="jps-tag jps-dark jps-tag-ricon">
            {{ lss('アップロード画像', 'Uploaded image') }}
            <BaseIcon size="size-6" name="mdi/close-circle" />
          </button>
        </span>
      </template>

      <!-- サブクエリ -->
      <SearchSubquery
        v-for="(sq, index) in ss.state.subquery"
        :key="index"
        :cs="ss.state.searchdef"
        :sub-query="sq"
        @edit="editSubQuery(sq)"
        @delete="ss.deleteSubQuery(sq)"
      />
      <button
        class="jps-text-button jps-search-box-query-button"
        v-if="canReset"
        @click="reset()"
      >
        {{ lss('リセット', 'Reset') }}
      </button>
      <slot name="query-addon" />
    </div>

    <template v-if="!noDetail">
      <!-- 検索フィールドボタン -->
      <div
        class="jps-search-detail-menu"
      >
        <div class="search-detail-menu-inner">
          <button
            class="jps-tag jps-border search-detail-menu-button"
            :class="{
              'jps-active is-active': isDetailActive(f.key),
              'jps-hidden is-hidden': f.hidden,
            }"
            v-for="f in detailMenuFields"
            :key="f.key"
            @click="toggleDetail(f.key)"
          >
            {{ ls(f.name) }}
            <BaseIcon
              :name="
                isDetailActive(f.key)
                  ? 'mdi/menu-up'
                  : 'mdi/menu-down'
              "
            />
          </button>

          <button
            class="jps-text-button search-detail-menu-toggle"
            v-if="hasMoreSearchFields && !editable"
            @click="showMoreSearchFields = !showMoreSearchFields"
          >
            {{
              showMoreSearchFields
                ? ls(dapLangGlobal.hide)
                : lss('全ての項目を表示', 'Show all fields')
            }}
          </button>
          <!-- 臨時フィールド -->
          <button
            class="jps-tag jps-border search-detail-menu-button"
            v-for="f in additionalFieldDefs"
            :class="{
              'jps-active is-active': isDetailActive(
                'af-' + f.key,
              ),
            }"
            :key="f.key"
            @click="toggleDetail('af-' + f.key)"
          >
            {{ ls(f.name) }}
            <BaseIcon
              :name="
                isDetailActive(f.key)
                  ? 'mdi/menu-up'
                  : 'mdi/menu-down'
              "
            />
          </button>
        </div>

        <template v-if="additional">
          <BaseDropdown
            class="dropdown-add-search"
            width="8rem"
            position="bottom-left"
          >
            <template v-slot:trigger="">
              <button
                type="button"
                class="jps-tag jps-border button-circle"
                aria-label="条件を追加"
              >
                <BaseIcon name="mdi/plus" />
              </button>
            </template>
            <BaseDropdownItem @click="editAdditionalField()">{{
              ls(labelAdditionalField)
            }}</BaseDropdownItem>
            <BaseDropdownItem @click="editSubQuery()">{{
              ls(labelSubSearch)
            }}</BaseDropdownItem>
          </BaseDropdown>

          <button
            type="button"
            class="jps-tag jps-border add-search-button"
            aria-label="条件を追加"
            @click="openAddMenu()"
          >
            <BaseIcon name="mdi/plus" />
          </button>
        </template>
        <!-- スロット -->
        <slot name="search-detail-menu-addon" />
      </div>

      <!-- 詳細フィールドコンポーネント -->
      <div class="jps-search-fields">
        <div
          class="search-field"
          v-for="f in filterdFields"
          :key="f.key"
          v-show="isDetailActive(f.key)"
        >
          <slot name="search-field-addon" :field="f" />
          <Component :is="searchFieldComponent(f)" :field="f" :ss="ss" />
          <UiButtonIcon
            @click="toggleDetail(f.key)"
            icon="mdi/window-close"
            arialabel="閉じる"
            class="close-detail-button jps-icon-button"
          />
        </div>

        <!-- 臨時追加フィールド -->
        <div
          class="search-field"
          v-for="f in additionalFieldDefs"
          :key="f.key"
          v-show="isDetailActive('af-' + f.key)"
        >
          <SearchFieldAdditional
            :field="f"
            :ss="ss"
            @delete="(v) => deleteAdditionalField(v)"
            @edit="(v) => editAdditionalField(v)"
          />
        </div>
        <div
          v-show="activeDetailKey.length > 0"
          class="closer"
        >
          <button @click="closeAllDetails()" class="jps-text-button">
            {{ lss('検索オプションを閉じる', 'Close Search Options') }}
          </button>
        </div>
      </div>
    </template>
    <UiOptionModal ref="optionModal" />
    <UiDbFieldSelectModal ref="dbFieldSelectModal" />
    <SearchSubqueryModal ref="searchSubqueryModal" />
  </div>
</template>

<script lang="ts" setup>
import type { UiOptionModal, UiDbFieldSelectModal, SearchSubqueryModal } from '#components'
import { LAST_UPDATE_TIMING_MAPPING, useSearchField } from '~/components/search/useSearchField'
import { IMF_FEATURE_CACHE } from '~/components/search/search-field/useBaseSearchField'
import BaseSectionContainer from '~/components/base/BaseSectionContainer.vue'
import { dapLangGlobal } from '#imports'

const MAX_DATABASE = 3
const MAX_SEARCH_FIELD = 10
const CURATION_ONLY_DETAIL_MENU_FIELD_KEYS = ['publicationLevel', 'ownerOrg']
interface ValueAndType {
  v: any
  type: 'f' | 'g' | 'q' | 'r' | 'e'
}

const { searchFieldComponent, getEra, getPrefecture } = useSearchField()
const { ls, lst, lss, lc, li } = useI18n()
const route = useRoute()
const { $getCodeRdfType, $getContentsCodeLang } = useNuxtApp()
const { otherNoImage } = useNoImage()

interface Props {
  ss: SearchStore<any>
  additional?: boolean
  editable?: boolean
  nodb?: boolean
  noDetail?: boolean
  formSize?: 'full' | 'middle'
  noDisplayQueryFields?: any
  showCurationDetailMenuFields?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  editable: false,
  additional: false,
  nodb: false,
  noDetail: false,
  formSize: 'full',
  noDisplayQueryFields: {},
  showCurationDetailMenuFields: false
})

const emits = defineEmits(['edit-constraint', 'delete-constraint', 'before-clear'])

const rdfTypeCode: Code = $getCodeRdfType()
const contentsCodeLang: {} = $getContentsCodeLang()

const showCurationDetailMenuFieldsEffective = () => {
  const path = route.path || ''
  return /^\/(en\/)?management/.test(path)
}

const cs = computed(() => {
  return props.ss.state.searchdef
})

// DB制約
const showMoreConstraints = ref(false)

const databaseIds = computed(() => {
  if (props.nodb) return []
  if (!cs.value.databaseIds) return []
  else if (!showMoreConstraints.value && !props.editable)
    return cs.value.databaseIds.slice(0, MAX_DATABASE)
  else return cs.value.databaseIds
})

const moreDbMsg = computed(() => {
  if (cs.value.databaseIds) {
    if (cs.value.databaseIds.length > databaseIds.value.length) {
      return lst(
        { ja: '他{}件', en: 'and {} more' },
        cs.value.databaseIds.length - databaseIds.value.length,
      )
    }
  }
})

// 中身検索
const isImageSearch = computed(() => {
  // return cs.value.keywordFields.includes('text2image')
  return (
    Array.isArray(cs.value?.keywordFields) &&
    cs.value.keywordFields.includes('text2image')
  )
})

//検索制約

const hasSearchConstraints = computed(() => {
  return !isEmptyArray(cs.value.constraints)
})

const hasMoreConstraints = computed(() => {
  return (
    (cs.value.databaseIds && cs.value.databaseIds.length > MAX_DATABASE) ||
    hasSearchConstraints.value
  )
})

const additionalFieldDefs = computed(() => {
  return props.ss.additionalFieldDefs || []
})

// 検索条件
const querydFields = computed(() => {
  return [...filterdFields.value, ...additionalFieldDefs.value]
})

//検索条件
function getValues(key: string): ValueAndType[] {
  const values = []
  if (props.ss.state.filter[key])
    values.push(
      ...props.ss.state.filter[key].map(v => {
        return { v, type: 'f' }
      }),
    )
  if (props.ss.state.query[key])
    values.push(
      ...props.ss.state.query[key].map(v => {
        return { v, type: 'q' }
      }),
    )
  if (props.ss.state.geoDistance[key])
    values.push(
      ...props.ss.state.geoDistance[key].map(v => {
        return { v, type: 'g' }
      }),
    )
  if (props.ss.state.range[key])
    values.push(
      ...props.ss.state.range[key].map(v => {
        return { v, type: 'r' }
      }),
    )
  if (props.ss.state.exists.length && props.ss.state.exists.includes(key))
    values.push({ key, type: 'e' })
  return values
}

const isDisplayQueryField = (key: string, valueAndType: ValueAndType): boolean => {
  return !(props.noDisplayQueryFields[key] && props.noDisplayQueryFields[key].includes(valueAndType?.v))
}

const getDisplayFilter = (filter: any): any => {
  let result = {}
  Object.entries(filter).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (props.noDisplayQueryFields[key]) {
        result[key] = value.filter(v => !props.noDisplayQueryFields[key].includes(v))
      } else {
        result[key] = value
      }
    }
  })
  return result
}

const getDatabaseId = (dbIdValue: string) => {
  if (dbIdValue.startsWith('+') || dbIdValue.startsWith('-'))
    return dbIdValue.substring(1)
  return dbIdValue
}

const dataStore = useDataStore()

const display = (f: CustomSearchField, v: ValueAndType) => {
  if (v.type === 'q') {
    let d = v.v.startsWith('+') || v.v.startsWith('-') ? v.v.substring(1) : v.v
    if (isExclude(v.v)) return displayWithExclude(d)
    return d
  } else if (v.type === 'f') {
    let d = v.v.startsWith('+') || v.v.startsWith('-') ? v.v.substring(1) : v.v
    if (f.type === 'contents') d = ls(contentsCodeLang[d])
    if (f.type === 'prefectures') d = ls(getPrefecture(d).name)
    if (f.type === 'temporal') d = ls(getEra(d).name)
    if (f.type === 'rdf_type') d = lc(rdfTypeCode, d)
    if (f.codeKey && dataStore.code(f.codeKey))
      d = lc(dataStore.code(f.codeKey), d)
    if (isExclude(v)) return displayWithExclude(d)
    return d
  } else if (v.type === 'r') {
    if (f.type === 'last_update') {
      return ls(LAST_UPDATE_TIMING_MAPPING[v.v.from])
    }
    return lst(
      { ja: '{}-{}' },
      v?.v?.from?.replace(/-/g, '/') || '',
      v?.v?.to?.replace(/-/g, '/') || '',
    )
  } else if (v.type === 'g') {
    return lst(
      { ja: '緯度{}, 経度{}, 半径{}', en: 'Lat.{}, Lon.{}, {} radius' },
      floor(v.v.lat, -4),
      floor(v.v.lon, -4),
      v.v.distance,
    )
  }
  return 'not_impl'
}

const dbDisplay = (db: Database | Organization, v: ValueAndType) => {
  if (db) {
    if (isExclude(v)) {
      return displayWithExclude(ls(db.name))
    }
    return ls(db.name)
  }
  return ''
}

const tagDisplay = (tag: Tag, v: ValueAndType) => {
  if (tag) {
    if (isExclude(v)) {
      return displayWithExclude(ls(tag.label))
    }
    return ls(tag.label)
  }
  return ''
}

const displayWithExclude = (v: string) => {
  return lst({ ja: '{}', en: '{}' }, v)
}

const isExclude = (v: ValueAndType) => {
  if (typeof v.v === 'string' && v.v.startsWith('-')) return true
}

const deselect = (f: string, v: ValueAndType) => {
  switch (v.type) {
    case 'f':
      props.ss.removeFilter(f, [v.v])
      break
    case 'e':
      props.ss.removeExist(f)
      break
    case 'g':
      props.ss.removeGeo(f)
      break
    case 'r':
      props.ss.removeRange(f, [v.v])
      break
    case 'q':
      props.ss.removeQuery(f, [v.v])
      break
  }
}

////////画像選択系
const removeImage = (id: string) => {
  removeFromArray(props.ss.state.image, id)
  props.ss.execute()
}

const removeTextImage = () => {
  props.ss.state.text2image = null
  props.ss.execute()
}

const imageInfo = computed(() => {
  return props.ss.state.image.map(i => {
    const isExternal = isExternalFeature(i)
    return {
      key: i,
      external: isExternal,
      image: isExternal ? (getLsCache(IMF_FEATURE_CACHE, i) as string) : null,
    }
  })
})

//リセット
const canReset = computed(() => {
  return (
    !isEmpty(props.ss.state.image) ||
    !isEmpty(props.ss.state.query) ||
    !isEmpty(props.ss.state.geoDistance) ||
    !isEmpty(props.ss.state.range) ||
    !isEmpty(props.ss.state.exists) ||
    !isEmpty(getDisplayFilter(props.ss.state.filter)) ||
    !isEmpty(props.ss.state.subquery) ||
    props.ss.state.text2image
  )
})

const reset = () => {
  emits('before-clear')
  props.ss.clearQuery()
  props.ss.execute()
}

//詳細項目関連
const activeDetailKey = ref([])

const showMoreSearchFields: Ref<boolean> = ref(false)

const hasMoreSearchFields = computed(() => {
  return filterdFields.value.length > MAX_SEARCH_FIELD
})

const filterdFields = computed(() => {
  return (
    cs.value?.searchFields
      ?.filter(sf => !sf.hidden || props.editable)
      .filter(
        sf =>
          sf.type !== 'tag' ||
          props.ss.state.result ||
          props.ss.state.fullFacets,
      ) || []
  )
})

const filterdFieldsLimit = computed(() => {
  if (showMoreSearchFields.value) return filterdFields.value
  else return filterdFields.value.slice(0, MAX_SEARCH_FIELD)
})

const detailMenuFields = computed(() => {
  return filterdFieldsLimit.value.filter((field) => {
    if (!showCurationDetailMenuFieldsEffective() &&
        CURATION_ONLY_DETAIL_MENU_FIELD_KEYS.includes(field.key)) {
      return false
    }
    return true
  })
})

const toggleDetail = (key: string) => {
  if (activeDetailKey.value.includes(key)) {
    removeFromArray(activeDetailKey.value, key)
  } else {
    activeDetailKey.value.push(key)
  }
}

const isDetailActive = (key: string) => {
  return activeDetailKey.value.includes(key)
}

const closeAllDetails = () => {
  activeDetailKey.value = []
}

//キーワード
const keyword: Ref<string> = ref('')
let inputKeywordRefresh: string = ''

const hasKeyword = computed(() => {
  return props.ss.state.searchdef.keywordFields?.length
})

const placeholder = computed(() => {
  return props.ss.state.searchdef.keywordPlaceholder
    ? ls(props.ss.state.searchdef.keywordPlaceholder)
    : ls(dapLangGlobal.mDefaultPlaceholder)
})

onBeforeMount(() => {
  if (hasKeyword.value) {
    props.ss.addQueryListenr(q => {
      if (props.ss.state.keyword) {
        keyword.value = props.ss.state.keyword.join(' ')
        inputKeywordRefresh = keyword.value
      } else {
        keyword.value = ''
      }
    })
    if (props.ss.state.keyword?.length > 0) {
      keyword.value = props.ss.state.keyword.join(' ')
      inputKeywordRefresh = keyword.value
    }
  }
})

const doSearch = () => {
  props.ss.state.from = 0
  props.ss.state.keyword = split(keyword.value)
  props.ss.execute()
}

const editAdditionalField = (target?: string) => {
  props.ss.editAdditionalField(dbFieldSelectModal.value, target, editAdditionalFieldHook)
}
const editSubQuery = (target?: SubQuery) => {
  props.ss.editSubQuery(searchSubqueryModal.value, target)
}

const deleteAdditionalField = (field: string) => {
  props.ss.deleteAdditionalField(field, deleteAdditionalFieldHook)
}

//AdditonalField関係
const labelAdditionalField = { ja: '検索項目', en: 'Additional Field' }
const labelSubSearch = { ja: 'サブ検索', en: 'Sub Search' }

const optionModal = ref<InstanceType<typeof UiOptionModal>>()
const dbFieldSelectModal = ref<InstanceType<typeof UiDbFieldSelectModal>>()
const searchSubqueryModal = ref<InstanceType<typeof SearchSubqueryModal>>()
const openAddMenu = async () => {
  try {
    const select = await optionModal.value.openAwait({
      message: { ja: '条件を追加', en: 'Add search options' },
      options: [
        { label: labelAdditionalField, option: 'af' },
        { label: labelSubSearch, option: 'ss' },
      ],
    })
    if (select === 'af') {
      editAdditionalField()
    } else {
      editSubQuery()
    }
  } catch (cancel) {}
}

const deleteAdditionalFieldHook = (field: string) => {
  removeFromArray(activeDetailKey.value, 'af-' + field)
}

const editAdditionalFieldHook = (field: string) => {
  activeDetailKey.value.push('af-' + field)
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-search-box {
  gap: 0.75rem;
  /* Editor 設定用レイアウト */
  .cs-editor-search-box & {
    display: grid;
    grid-template-rows: auto auto auto auto;
    .keyword-line {
      grid-row: 2 / 3;
    }
  }
}

.keyword-line {
  .cs-editor & {
    border: 1px solid $jps-gray-400;
    border-radius: $global-radius;
    background: $jps-gray-50;
  }

  .keyword-line-inner {
    display: flex;
    justify-content: center;
    align-items: center;

    .cs-editor & {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 1rem;
    }
  }

  .jps-keyword-field {
    max-width: 50%;
    min-width: 30rem;
    padding: 0 0.5rem;
    background-color: $white;
    border: solid 1px $jps-gray-600;
    border-radius: 9999px;
    display: flex;
    justify-content: flex-start;
    flex-grow: 1;
    align-items: center;
    @include focus-within();

    @include is-mobile {
      min-width: 10rem;
      max-width: 100%;
    }
  }

  .jps-keyword-input {
    width: 100%;
    font-size: 1rem;
    height: 2.5em;
    line-height: 1.5;
    position: relative;
    vertical-align: top;
    padding: 0.25rem;
    border: none;
    background-color: transparent;

    &:is(input:placeholder-shown),
    &:is(input::-webkit-input-placeholder) {
      color: $jps-gray-600;
    }
  }

  .jps-keyword-search-button-list {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 0.25rem;
  }

  .jps-keyword-search-button {
    cursor: pointer;
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    @at-root .hina & {
      color: $black;
    }
  }

  .jps-keyword-search-button-inner {
    max-width: 1.5rem;
    max-height: 1.5rem;
  }

}

.queries {
  display: flex;
  align-items: center;
  gap: 1.25rem 0.75rem;
  width: 100%;
  max-width: $wide-content-width;
  margin-left: auto;
  margin-right: auto;
  flex-wrap: wrap;

  @include is-tablet {
    min-width: 24rem;
  }

  .cs-editor & {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    margin: inherit;
    margin-bottom: 1rem;
    border: 1px solid $jps-gray-400;
    padding: 1rem;
    border-radius: $global-radius;
    background: $jps-gray-50;
    max-width: none;
  }

  .jps-tag {
    margin-right: 0 !important;
    height: auto;
    min-height: 26px;
    display: inline;
    max-width: 100%;

    > *:first-child {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .colon {
      width: 1rem;
    }

    &.target-db {
      cursor: default;

      @at-root .jps:not(.hina) & {
        background-color: $jps-sub-red;
        border-color: $jps-sub-red;
        color: $white;
        &:hover,
        &:visited,
        &:visited:hover {
          background-color: $jps-sub-red !important;
          color: $white !important;
        }
      }

      @at-root .hina & {
        background-color: $hina-accent-lime;
        color: $black;
      }
    }
    &.constraint {
      cursor: default;
      background-color: $jps-gray-50;
      border-color: $jps-gray-100;
      color: $text;
      &:hover,
      &:visited,
      &:visited:hover {
        background-color: $jps-gray-50 !important;
        color: $text !important;
      }
    }
    .cs-editor-search-box &.constraint {
      border-color: $jps-gray-300;
    }
  }
  .image-search-query {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem 0.25rem;

    .selected-image {
      border-radius: $global-radius;
      max-height: 4rem;
      max-width: 8rem;
      flex-shrink: 0;
    }
  }
  .splitter {
    margin-top: 0.5rem;
    width: 1px;
    height: 0.875rem;
    border-right: 1px solid $text;
  }
}

.jps-search-box-query-button {
    font-size: 0.75rem;
    line-height: 1rem;

    &.image-search-button {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;

      .text {
        line-height: 1.5;
        overflow-wrap: anywhere;
        white-space: normal;
      }

      .icon {
        line-height: 1.5;
        flex-shrink: 0;
      }
    }
}

.jps-search-detail-menu {
  max-width: $wide-content-width;
  margin-left: auto;
  margin-right: auto;
  min-width: 24rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;

  width: 100%;
  flex-wrap: nowrap;
  overscroll-behavior: contain;
  padding-bottom: 1rem;

  @include is-mobile {
    overflow-x: scroll;
    min-width: 12rem;
    justify-content: flex-start
  }

  .search-detail-menu-inner {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;

    @include is-mobile {
      flex-wrap: nowrap;
    }
  }

  .cs-editor & {
    margin-left: inherit;
    flex-direction: column;
    align-items: flex-start;
    border: 1px solid $jps-gray-400;
    padding: 1rem;
    border-radius: $global-radius;
    background: $jps-gray-50;
    max-width: none;
    width: 100%;
  }

  .jps-tag {
    cursor: pointer;
    margin-right: 0 !important;
    background-color: $white;
    transition-duration: 0s;
    padding-right: 5px;

    @at-root .hina & {
      background-color: $hina-background-cream;
    }
    &.button-circle {
      padding-left: 5px;
    }
    &.jps-active {
      background-color: $jps-gray-50;
      border-color: $jps-key-blue;
    }
    &.jps-hidden {
      background-color: $jps-gray-500;
      border-color: $jps-gray-600;
    }
  }
  > .jps-text-button {
    margin-right: 0.75rem;
  }

  .dropdown-add-search {
    display: none;
    @include is-tablet {
      display: block;
    }
  }

  .add-search-button {
    padding: 0.25rem;
    @include is-tablet {
      display: none;
    }
  }

  @include is-mobile {
    flex-wrap: nowrap;
    overflow-x: scroll;
    padding-bottom: 1rem;
    overscroll-behavior: contain;
  }
}

.search-detail-menu-button {
  cursor: pointer;
  background-color: $white;
  padding-right: 0.25rem;
  transition-property: none;

  @at-root .hina & {
    :deep(svg) {
      color: $hina-accent-green;
    }

    &.is-active {
      background-color: $hina-background-cream;
      outline: 1px solid $hina-accent-green;
      border-color: $hina-accent-green;
    }

    &.is-hidden {
      background-color: $hina-background-cream;
      border-color: $hina-background-cream;
    }
  }

  &.is-active {
    border-color: rgb(37 99 235);
    background-color: $jps-gray-50;
  }

  &.is-hidden {
    border-color: $jps-gray-600;
    background-color: $jps-gray-500;
  }
}

.search-detail-menu-toggle {
  font-size: 0.75rem;
  line-height: 1rem;
  cursor: pointer;
  background-color: $white;
  padding-right: 0.25rem;
  transition-property: none;
  flex-shrink: 0;
}

.jps-search-fields {
  max-width: $wide-content-width;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: auto;
  row-gap: 1rem;

  .search-field {
    min-height: 2.25rem;
    border: 1px solid $border-grey;
    border-radius: $global-radius-large;
    @include box-shadow();
    position: relative;
    overflow: hidden;

    .close-detail-button {
      position: absolute;
      top: 6px;
      right: 8px;
      color: $jps-gray-600;
      width: 16px;
      svg {
        width: 16px;
      }
    }
  }
  .closer {
    width: 100%;
    text-align: right;

    button {
      padding: 0.25rem;
      font-size: $size-7;
    }
  }
}
</style>
